import {
    ChangeEvent,
    Dispatch,
    SetStateAction,
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import {
    Button,
    Checkbox,
    ChevronDownIcon,
    Controller,
    RadioButtonGroup,
    TextField,
} from '@selina-finance/ui';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@App/contexts/Application';
import {
    getDefaultValues,
    getFormHelpers,
    getValidationSchema,
    handleSubFormValidation,
    radioButtonOptions,
    radioButtonOptionsInvert,
} from '@Shared/helpers/input';
import AddressRepeaterView from '@Views/eligibility/addressRepeaterView/AddressRepeaterView';
import { AuthContext } from '@src/app/contexts/Auth';
import {
    Nationalities,
    formItems,
} from '@src/app/formModels/eligibility/applicantDetails';
import { useKameleoonFeature } from '@src/app/hooks/useKameleoon';
import { ExperimentsConfig } from '@src/app/utils/experiments';
import { KAMELEOON_FEATURE } from '@src/app/utils/kameleoon';
import CallOut from '@src/ui/components/CallOut/Callout';
import CreditScoreDisclaimer from '@src/ui/components/CreditScoreDisclaimer/CreditScoreDisclaimer';
import { HttpError } from '@src/ui/components/HttpError';

import ErrorContainer from '../../components/ErrorContainer/ErrorContainer';
import SelectBox from '../../components/SelectBox/SelectBox';

import { getKameleoonVariantWithRetry } from './getKameleoonVariantWithRetry';

interface ApplicantViewProps {
    formRef: any;
    saveRef: any;
    className?: string;
    isPrimaryApplicant: boolean;
    applicantData?: any;
    saveApplicants: (
        data: any,
        isPrimaryApplicant: boolean,
        noOfApplicants: number,
    ) => Promise<any>;
    mobilePhoneNumber?: string;
    emailAddress?: string;
    setActiveApplicant: Dispatch<SetStateAction<number>>;
    setApplicantCount?: Dispatch<SetStateAction<number>>;
    applicantCount: number;
}

const defaultValues = getDefaultValues(formItems);
const validationSchema = getValidationSchema(formItems);

const ApplicantView = forwardRef((props: ApplicantViewProps) => {
    const formikRef = useRef<any>(null);
    const hasAddressItemAddedRef = useRef(false);

    const {
        setCurrentForm,
        externalApplicationId,
        applicationId,
        updatePhoneNumber,
        loanDetails,
        saveLoanDetails,
        deferredApplicant2TestUser,
        setDeferredApplicant2TestUser,
    } = useContext(ApplicationContext);

    const { loggedIn } = useContext(AuthContext);

    const getA2DeferVariationKey = useKameleoonFeature(
        KAMELEOON_FEATURE.a2_defer,
    );

    const [error, setError] = useState(null);

    const [consent, setConsent] = useState<boolean>(() => {
        const storedConsent = localStorage.getItem(
            `consent - ${applicationId}`,
        );
        return storedConsent ? JSON.parse(storedConsent) : false;
    });

    const [consentError, setConsentError] = useState(false);

    const navigate = useNavigate();

    const getExternalApplicationId = externalApplicationId
        ? externalApplicationId
        : localStorage.getItem('externalApplicationId');

    // avoids validation failing for 2nd applicant
    if (!props.isPrimaryApplicant) {
        props.applicantData.hasSecondApplicant = false;
    }

    useEffect(() => {
        if (props.isPrimaryApplicant && loanDetails.amount && !loggedIn) {
            saveLoanDetails(loanDetails).then((data: any) => {
                if (!data.success) {
                    setError(data.error);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanDetails.amount, loggedIn]);

    useEffect(() => {
        formikRef.current.values = {
            ...defaultValues,
            ...props.applicantData,
        };
    }, [props.applicantData]);

    useEffect(() => {
        setCurrentForm(undefined);
    }, [setCurrentForm]);

    useEffect(() => {
        if (formikRef.current) {
            setCurrentForm(formikRef.current);
        }
    }, [formikRef, setCurrentForm]);

    const validateAndSubmit = async () => {
        if (!props.isPrimaryApplicant && !consent) {
            setConsentError(true);
            return;
        }

        setError(null);
        const isValid = await handleSubFormValidation(formikRef.current);
        if (!isValid) {
            return false;
        }

        const values = formikRef.current.values;

        if (
            values.mobilePhoneNumber &&
            getExternalApplicationId &&
            values.mobilePhoneNumber !== props.applicantData.mobilePhoneNumber
        ) {
            await updatePhoneNumber(
                values.mobilePhoneNumber,
                getExternalApplicationId,
                applicationId,
            ).then((data: any) => {
                if (!data.success) {
                    setError(data.error);
                    return false;
                }
            });
        }

        props
            .saveApplicants(
                values,
                props.isPrimaryApplicant,
                props.applicantCount,
            )
            .then(async (data: any) => {
                const currentPath = window.location.pathname;

                if (
                    currentPath === '/eligibility/applicants' &&
                    data.hasCoApplicant === true
                ) {
                    const variant = await getKameleoonVariantWithRetry(
                        getA2DeferVariationKey,
                    );
                    const isVariantB = variant === ExperimentsConfig.Variants.B;

                    setDeferredApplicant2TestUser(isVariantB);
                    if (variant !== null) {
                        mixpanel.register({ a2_defer_variant: variant });
                    }
                    mixpanel.track('experiment_assigned', {
                        a2_defer_variant: variant,
                    });
                    navigate(
                        isVariantB
                            ? '/eligibility/property-details'
                            : '/eligibility/applicants2',
                    );
                    return true;
                }

                if (
                    currentPath === '/eligibility/applicants2' &&
                    deferredApplicant2TestUser &&
                    data.success
                ) {
                    navigate('/eligibility/income2');
                    return true;
                }

                const redirectTo =
                    values.redirectTo || '/eligibility/property-details';

                if (data.success && !data.hasCoApplicant) {
                    if (currentPath === '/eligibility/applicants') {
                        setDeferredApplicant2TestUser(null);
                    }
                    navigate(redirectTo);
                    return true;
                }

                if (!data.success) {
                    setError(data.error);
                    return false;
                }
            });

        return false;
    };

    const saveForLater = async () => {
        hasAddressItemAddedRef.current = false;

        const values = {
            ...formikRef.current.values,
            addresses: formikRef.current.values.addresses.filter(
                (it: any) => !isEmpty(it.address),
            ),
        };

        if (
            values.mobilePhoneNumber &&
            getExternalApplicationId &&
            values.mobilePhoneNumber !== props.applicantData.mobilePhoneNumber
        ) {
            await updatePhoneNumber(
                values.mobilePhoneNumber,
                getExternalApplicationId,
                applicationId,
            ).then((data: any) => {
                if (!data.success) {
                    setError(data.error);
                    return false;
                }
            });
        }

        await props
            .saveApplicants(
                values,
                props.isPrimaryApplicant,
                props.applicantCount,
            )
            .then((data: any) => {
                if (data.success) {
                    return true;
                }
                if (!data.success) {
                    setError(data.error);
                    return false;
                }
            });
    };

    useImperativeHandle(props.formRef, () => {
        return { validateAndSubmit };
    });

    useImperativeHandle(props.saveRef, () => ({
        saveMethod: saveForLater,
    }));

    const showPhoneNumber =
        !props.applicantData.mobilePhoneNumber || !props.isPrimaryApplicant;

    const handleCitizenChange = (
        e: ChangeEvent<HTMLInputElement>,
        formikProps: any,
    ) => {
        const { setTouched, setFieldValue, handleChange, touched } =
            formikProps;

        setTouched({ ...touched, kqUkResidency: true });

        if (e.target.value === 'true') {
            setFieldValue('notHavePermanentRightToResideInTheUk', null);
            setFieldValue('nationality', null);
        }

        handleChange(e);
    };

    const handleConsentChange = () => {
        setConsent((prevConsent) => {
            localStorage.setItem(
                `consent - ${applicationId}`,
                JSON.stringify(!prevConsent),
            );
            return !prevConsent;
        });

        setConsentError(false);
    };

    const handleSecondApplicantChange = (
        e: ChangeEvent<HTMLInputElement>,
        formikProps: any,
    ) => {
        const { setTouched, handleChange, touched } = formikProps;

        setTouched({
            ...touched,
            hasSecondApplicant: true,
        });
        props.setApplicantCount &&
            props.setApplicantCount(e.target.value === 'true' ? 2 : 1);
        handleChange(e);

        localStorage.setItem(
            `hasSecondApplicantSelected${applicationId}`,
            'true',
        );
    };

    return (
        <div className={props.className}>
            <Formik
                enableReinitialize
                initialValues={{
                    ...defaultValues,
                    ...props.applicantData,
                    primaryApplicantPhoneNumber: props.mobilePhoneNumber,
                    primaryApplicantEmail: props.emailAddress,
                }}
                validationSchema={validationSchema}
                onSubmit={validateAndSubmit}
                innerRef={(ref) => (formikRef.current = ref)}
            >
                {(formikProps) => {
                    const { getError } = getFormHelpers(formItems, formikProps);

                    // dont validate field for 2nd applicant
                    if (!props.isPrimaryApplicant) {
                        delete formikProps.errors.hasSecondApplicant;
                    }
                    const addressesError = getError(`addresses`);

                    let toDateError = '';
                    if (Array.isArray(addressesError)) {
                        addressesError?.forEach((it: any, index: number) => {
                            const toDate =
                                formikProps.values.addresses[index]?.livedIn.to;
                            if (toDate) {
                                toDateError = it?.livedIn?.to;
                            }
                        });
                    }
                    const additionalError =
                        typeof addressesError === 'string'
                            ? addressesError
                            : toDateError;

                    return (
                        <Form>
                            <div className='grid grid-cols-1 mt-6 mb-6 gap-10'>
                                {!props.isPrimaryApplicant && (
                                    <SelectBox
                                        small='true'
                                        items={formItems.title.items}
                                        suffix={<ChevronDownIcon />}
                                        label='Title'
                                        name='title'
                                        validState={!!formikProps.values.title}
                                        error={getError('title')}
                                        value={formikProps.values.title || ' '}
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) => {
                                            formikProps.setFieldValue(
                                                'title',
                                                e.currentTarget.value,
                                            );
                                        }}
                                        onBlur={() => {
                                            return formikProps.setTouched({
                                                ...formikProps.touched,
                                                title: true,
                                            });
                                        }}
                                    />
                                )}

                                {!props.isPrimaryApplicant && (
                                    <>
                                        <div>
                                            <p className='text-body font-bold'>
                                                {`${
                                                    props.isPrimaryApplicant
                                                        ? 'Your'
                                                        : 'Their'
                                                } name`}
                                            </p>
                                            <CallOut>
                                                <div className='leading-5 text-sm_body'>
                                                    <p>
                                                        {`As it appears on ${
                                                            props.isPrimaryApplicant
                                                                ? 'your'
                                                                : 'their'
                                                        } passport or driving license.`}
                                                    </p>
                                                </div>
                                            </CallOut>
                                            <Controller>
                                                <div className='grid gap-6 mt-2'>
                                                    <TextField
                                                        labelSize='small'
                                                        label='First name'
                                                        name='firstName'
                                                        className='mp-mask'
                                                        maxLength={255}
                                                        value={
                                                            formikProps.values
                                                                .firstName || ''
                                                        }
                                                        onChange={(
                                                            e: ChangeEvent<HTMLInputElement>,
                                                        ) =>
                                                            formikProps.handleChange(
                                                                e,
                                                            )
                                                        }
                                                        onBlur={
                                                            formikProps.handleBlur
                                                        }
                                                        error={getError(
                                                            'firstName',
                                                        )}
                                                    />

                                                    <TextField
                                                        labelSize='small'
                                                        label='Middle name'
                                                        name='middleName'
                                                        className='mp-mask'
                                                        maxLength={255}
                                                        value={
                                                            formikProps.values
                                                                .middleName ||
                                                            ''
                                                        }
                                                        onChange={(
                                                            e: ChangeEvent<HTMLInputElement>,
                                                        ) =>
                                                            formikProps.handleChange(
                                                                e,
                                                            )
                                                        }
                                                        onBlur={
                                                            formikProps.handleBlur
                                                        }
                                                        error={getError(
                                                            'middleName',
                                                        )}
                                                    />

                                                    <TextField
                                                        labelSize='small'
                                                        label='Last name'
                                                        name='lastName'
                                                        className='mp-mask'
                                                        maxLength={255}
                                                        value={
                                                            formikProps.values
                                                                .lastName || ''
                                                        }
                                                        onChange={(
                                                            e: ChangeEvent<HTMLInputElement>,
                                                        ) =>
                                                            formikProps.handleChange(
                                                                e,
                                                            )
                                                        }
                                                        onBlur={
                                                            formikProps.handleBlur
                                                        }
                                                        error={getError(
                                                            'lastName',
                                                        )}
                                                    />
                                                </div>
                                            </Controller>
                                        </div>
                                    </>
                                )}

                                {!props.isPrimaryApplicant && (
                                    <TextField
                                        name='dateOfBirth'
                                        className='mp-mask'
                                        placeholder='DD/MM/YYYY'
                                        label='Date of birth'
                                        mask='date'
                                        value={
                                            formikProps.values.dateOfBirth || ''
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) => formikProps.handleChange(e)}
                                        onBlur={formikProps.handleBlur}
                                        error={getError('dateOfBirth')}
                                    />
                                )}

                                {showPhoneNumber && (
                                    <TextField
                                        label='Phone number'
                                        placeholder='Mobile Preferred'
                                        name='mobilePhoneNumber'
                                        className='mp-mask'
                                        value={
                                            formikProps.values
                                                .mobilePhoneNumber || ''
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) => formikProps.handleChange(e)}
                                        onBlur={formikProps.handleBlur}
                                        error={getError('mobilePhoneNumber')}
                                    />
                                )}

                                {!props.isPrimaryApplicant && (
                                    <TextField
                                        label='Email address'
                                        name='emailAddress'
                                        className='mp-mask'
                                        maxLength={255}
                                        value={
                                            formikProps.values.emailAddress ||
                                            ''
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) => formikProps.handleChange(e)}
                                        onBlur={formikProps.handleBlur}
                                        error={getError('emailAddress')}
                                    />
                                )}

                                <Controller>
                                    <SelectBox
                                        items={formItems.maritalStatus.items}
                                        suffix={<ChevronDownIcon />}
                                        label={`What's ${
                                            props.isPrimaryApplicant
                                                ? 'your'
                                                : 'their'
                                        } marital status`}
                                        name='maritalStatus'
                                        validState={
                                            !!formikProps.values.maritalStatus
                                        }
                                        error={getError('maritalStatus')}
                                        value={
                                            formikProps.values.maritalStatus?.toString() ||
                                            ''
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLSelectElement>,
                                        ) => {
                                            formikProps.setFieldValue(
                                                'maritalStatus',
                                                e.currentTarget.value,
                                            );
                                        }}
                                    />
                                </Controller>

                                <Controller
                                    label={`Are ${
                                        props.isPrimaryApplicant
                                            ? 'you'
                                            : 'they'
                                    } a British citizen?`}
                                >
                                    <RadioButtonGroup
                                        name='kqUkResidency'
                                        id='kqUkResidency'
                                        value={formikProps.values.kqUkResidency?.toString()}
                                        error={{
                                            hasError: false,
                                            errorMessage:
                                                getError('kqUkResidency'),
                                        }}
                                        onChange={(e) =>
                                            handleCitizenChange(e, formikProps)
                                        }
                                        options={radioButtonOptions}
                                    />
                                </Controller>

                                {formikProps.values.kqUkResidency ===
                                    'false' && (
                                    <SelectBox
                                        items={Nationalities}
                                        suffix={<ChevronDownIcon />}
                                        label={`What's ${
                                            props.isPrimaryApplicant
                                                ? 'your'
                                                : 'their'
                                        } primary citizenship?`}
                                        name='nationality'
                                        validState={
                                            !!formikProps.values.nationality
                                        }
                                        error={getError('nationality')}
                                        value={
                                            formikProps.values.nationality?.toString() ||
                                            ' '
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLSelectElement>,
                                        ) => {
                                            formikProps.setFieldValue(
                                                'nationality',
                                                e.currentTarget.value,
                                            );
                                        }}
                                        onBlur={() => {
                                            return formikProps.setTouched({
                                                ...formikProps.touched,
                                                nationality: true,
                                            });
                                        }}
                                    />
                                )}

                                {formikProps.values.kqUkResidency ===
                                    'false' && (
                                    <Controller
                                        label={`Do ${
                                            props.isPrimaryApplicant
                                                ? 'you'
                                                : 'they'
                                        } have permanent rights to reside in the UK?`}
                                    >
                                        <RadioButtonGroup
                                            name='notHavePermanentRightToResideInTheUk'
                                            id='notHavePermanentRightToResideInTheUk'
                                            value={formikProps.values.notHavePermanentRightToResideInTheUk?.toString()}
                                            error={{
                                                hasError: false,
                                                errorMessage: getError(
                                                    'notHavePermanentRightToResideInTheUk',
                                                ),
                                            }}
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) => {
                                                formikProps.setTouched({
                                                    ...formikProps.touched,
                                                    notHavePermanentRightToResideInTheUk:
                                                        true,
                                                });
                                                formikProps.handleChange(e);
                                            }}
                                            options={radioButtonOptionsInvert}
                                        />
                                    </Controller>
                                )}

                                <Controller
                                    label={`Do ${
                                        props.isPrimaryApplicant
                                            ? 'you'
                                            : 'they'
                                    } have any criminal convictions?`}
                                >
                                    <RadioButtonGroup
                                        name='hasAnyCriminalConvictions'
                                        id='hasAnyCriminalConvictions'
                                        value={formikProps.values.hasAnyCriminalConvictions?.toString()}
                                        error={{
                                            hasError: false,
                                            errorMessage: getError(
                                                'hasAnyCriminalConvictions',
                                            ),
                                        }}
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) => {
                                            formikProps.setTouched({
                                                ...formikProps.touched,
                                                hasAnyCriminalConvictions: true,
                                            });
                                            formikProps.handleChange(e);
                                        }}
                                        options={radioButtonOptions}
                                    />
                                </Controller>

                                <Controller
                                    label={`${
                                        props.isPrimaryApplicant
                                            ? 'Your address history'
                                            : '2nd applicant address history'
                                    } `}
                                    additionalInfo={`We need to gather ${
                                        props.isPrimaryApplicant
                                            ? 'your'
                                            : 'their'
                                    } past 3 years address history as part of our lending criteria.`}
                                >
                                    <AddressRepeaterView
                                        addressFieldName={'addresses'}
                                        hasAddressItemAddedRef={
                                            hasAddressItemAddedRef
                                        }
                                        formikProps={formikProps}
                                        formItems={formItems}
                                        isPrimaryApplicant={
                                            props.isPrimaryApplicant
                                        }
                                    />
                                </Controller>

                                {props.isPrimaryApplicant && (
                                    <Controller label='Do you own the property with anyone else?'>
                                        <CallOut classes='mb-4'>
                                            <div className='leading-5 text-sm_body'>
                                                <p className='mb-5'>
                                                    Selina provides second
                                                    charge mortgages (also known
                                                    as secured loans) offering
                                                    higher loan values and lower
                                                    monthly repayments
                                                </p>
                                                <p>
                                                    So we need to know about
                                                    anyone else you own the
                                                    property with — on either
                                                    the mortgage or title deeds.
                                                </p>
                                            </div>
                                        </CallOut>

                                        <RadioButtonGroup
                                            name='hasSecondApplicant'
                                            id='hasSecondApplicant'
                                            value={
                                                localStorage.getItem(
                                                    `hasSecondApplicantSelected${applicationId}`,
                                                ) === 'true'
                                                    ? formikProps.values.hasSecondApplicant?.toString()
                                                    : undefined
                                            }
                                            error={{
                                                hasError: false,
                                                errorMessage:
                                                    getError(
                                                        'hasSecondApplicant',
                                                    ),
                                            }}
                                            onChange={(e) =>
                                                handleSecondApplicantChange(
                                                    e,
                                                    formikProps,
                                                )
                                            }
                                            options={radioButtonOptions}
                                        />
                                    </Controller>
                                )}

                                {!props.isPrimaryApplicant && (
                                    <div className='flex flex-col text-md'>
                                        <div className='flex items-center'>
                                            <Checkbox
                                                name='applicant-consent'
                                                checked={consent}
                                                onChange={handleConsentChange}
                                                error={
                                                    consentError
                                                        ? {
                                                              hasError: true,
                                                              errorMessage:
                                                                  'Please make sure your co-applicant has given their consent before continuing.',
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <div className='w-full font-normal font-md'>
                                                    I confirm that I have
                                                    consent from this person to:
                                                    <ul className='list-disc pl-5 mt-3'>
                                                        <li>
                                                            Submit this
                                                            application on their
                                                            behalf
                                                        </li>
                                                        <li>
                                                            Give their personal
                                                            data to Selina
                                                            Finance and link it
                                                            to my own
                                                        </li>
                                                        <li>
                                                            Conduct a soft
                                                            credit search for
                                                            both of us
                                                        </li>
                                                    </ul>
                                                </div>
                                            </Checkbox>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {error && <HttpError error={error} />}

                            <ErrorContainer
                                formikProps={formikProps}
                                additionalErrors={[additionalError]}
                            />

                            <Button
                                color='secondary'
                                type='submit'
                                size='large'
                                className='w-full mt-12 mb-6'
                            >
                                Continue
                            </Button>

                            {!props.isPrimaryApplicant && (
                                <CreditScoreDisclaimer secondApplicant />
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
});

export default ApplicantView;
